import { UserProfile } from '@/types';
import { logger } from '@shared/logging/logger';

export type AuthRole = 'citizen' | 'collector' | 'company' | 'attendant' | 'manager' | 'unknown';

export interface RoleDefinition {
  name: AuthRole;
  title: string;
  description: string;
  inherits?: AuthRole[];
  permissions: string[];
}

export const ROLES: Record<AuthRole, RoleDefinition> = {
  citizen: {
    name: 'citizen',
    title: 'Cidadão',
    description: 'Usuário comum do ecossistema que realiza descartes e consome conteúdos.',
    permissions: [
      'shared:view_profile',
      'shared:update_profile',
      'shared:view_notifications',
      'app:view_ranking',
      'app:view_quiz',
      'app:perform_discard',
      'app:recycling_history',
      'app:claim_rewards',
    ],
  },
  collector: {
    name: 'collector',
    title: 'Catador',
    description: 'Profissional autônomo que coleta resíduos e gerencia frotas/pontos.',
    inherits: ['citizen'],
    permissions: [
      'app:collect_waste',
      'app:manage_collections',
    ],
  },
  company: {
    name: 'company',
    title: 'Empresa',
    description: 'Parceiro comercial ou gerador de grande volume de resíduos.',
    inherits: ['citizen'],
    permissions: [
      'app:request_collection',
      'app:view_reports',
    ],
  },
  attendant: {
    name: 'attendant',
    title: 'Atendente',
    description: 'Operador de Ecoponto que valida entregas e pesa resíduos.',
    permissions: [
      'shared:view_profile',
      'shared:update_profile',
      'shared:view_notifications',
      'app:view_ranking',
      'app:view_quiz',
      'app:register_discard',
      'app:validate_transaction',
      'app:view_ecoponto_status',
    ],
  },
  manager: {
    name: 'manager',
    title: 'Administrador',
    description: 'Gestor da plataforma com controle total operacional de governança e auditoria.',
    permissions: ['*'], // Superuser wildcard matching everything
  },
  unknown: {
    name: 'unknown',
    title: 'Não Identificado',
    description: 'Perfil convidado sem autenticação validada.',
    permissions: [],
  },
};

/**
 * Enterprise Role and Permission Matrix Validator.
 * Computes permission checks taking hierarchy, wildcards, and edge cases into account.
 */
class RoleManager {
  /**
   * Resolves all permissions (including inherited ones) for a given role.
   */
  public getPermissionsForRole(role: AuthRole): Set<string> {
    const list = new Set<string>();
    const visited = new Set<AuthRole>();

    const traverse = (r: AuthRole) => {
      if (visited.has(r)) return;
      visited.add(r);

      const def = ROLES[r];
      if (!def) return;

      // Add direct permissions
      def.permissions.forEach((p) => list.add(p));

      // Traverse inheritance
      if (def.inherits) {
        def.inherits.forEach((parent) => traverse(parent));
      }
    };

    traverse(role);
    return list;
  }

  /**
   * Verifies if a role has a specified permission.
   */
  public hasPermission(role: AuthRole, permission: string): boolean {
    const permissions = this.getPermissionsForRole(role);
    
    // Superuser permission evaluation
    if (permissions.has('*')) {
      return true;
    }

    // Exact matches
    if (permissions.has(permission)) {
      return true;
    }

    // Namespace checking, e.g., if checking for "app:view_ranking" and role has "app:*"
    const [namespace] = permission.split(':');
    if (namespace && permissions.has(`${namespace}:*`)) {
      return true;
    }

    return false;
  }

  /**
   * Maps UserProfile enum values to AuthRole string handles.
   */
  public mapProfileToRole(profileType: UserProfile | null, adminSector?: string): AuthRole {
    if (!profileType) return 'unknown';

    // If there is an admin sector, override or refine role
    if (adminSector) {
      if (adminSector === 'Atendente') {
        return 'attendant';
      }
      if (['Administrador', 'Gestor', 'Diretor'].some(s => adminSector.startsWith(s))) {
        return 'manager';
      }
    }

    // Standard mappings
    switch (profileType) {
      case UserProfile.CITIZEN:
        return 'citizen';
      case UserProfile.COLLECTOR:
        return 'collector';
      case UserProfile.COMPANY:
        return 'company';
      case UserProfile.ATTENDANT:
        return 'attendant';
      case UserProfile.MANAGER:
        return 'manager';
      default:
        return 'unknown';
    }
  }
}

export const roleManager = new RoleManager();
